import React, { useMemo, useState } from 'react';

import type { HubItem, HubItemKind } from '../api/types';

import { EmptyNote, FilterChips, PageHeader, SearchField } from '../components/Chrome';
import ContentLoader from '../components/ContentLoader';
import ContentMessage from '../components/ContentMessage';
import HubItemDispatch from '../components/HubItemDispatch';
import HubItemPlate from '../components/HubItemPlate';
import TopicRail, { type TopicCount } from '../components/TopicRail';
import { useHubArchive } from '../hooks/useHubArchive';

// News and articles are split on lifecycle, and that's exactly what their listings show.
// News is perishable, so it reads as a dated feed of dispatches — plate left, record right,
// scanned downward. An article stays useful for years, so the library is browsed by topic:
// a rail states the shape of the collection and the plates give each piece a column.
const COPY = {
  article: {
    heading: 'Guides, how-tos and reference pieces.',
    intro: 'The article library, browsed by topic.',
    searchLabel: 'Search articles by title…',
    empty: 'No articles have been published yet.',
    noMatch: 'No articles match your search.',
    error: 'The articles couldn’t be loaded. Please try again later.',
    uncategorised: 'Other',
    allLabel: 'All topics',
  },
  news: {
    heading: 'Dispatches',
    intro: 'Announcements, notices and bulletins from Natshoot, newest first.',
    searchLabel: 'Search news by title…',
    empty: 'No news has been published yet.',
    noMatch: 'No news matches your search.',
    error: 'The news feed couldn’t be loaded. Please try again later.',
    uncategorised: 'Other',
    allLabel: 'All records',
  },
} as const;

/**
 * Every category present in the set, with its tally.
 *
 * Counted from the loaded items rather than asked of the server: the whole active set is
 * already here, so a second round trip would only re-derive what is in memory. Uncategorised
 * sorts last — a drafted-but-unfiled piece shouldn't lead the rail.
 */
const countTopics = (items: HubItem[], uncategorised: string): TopicCount[] => {
  const counts = new Map<string, number>();
  for (const item of items) {
    const key = item.categoryTitle || uncategorised;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => {
      if (a.label === uncategorised) {
        return 1;
      }
      if (b.label === uncategorised) {
        return -1;
      }
      return a.label.localeCompare(b.label);
    });
};

interface HubArchiveProps {
  kind: HubItemKind;
}

const HubArchive: React.FC<HubArchiveProps> = ({ kind }) => {
  const { items, state } = useHubArchive(kind);
  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState<string | null>(null);
  const copy = COPY[kind];

  // Low volume: the full active set is loaded once, so search and topic both filter
  // client-side without another round trip — the same trade the newsletter archive makes.
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((i) => {
      const matchesTerm = !term || i.title.toLowerCase().includes(term);
      const matchesTopic = !topic || (i.categoryTitle || copy.uncategorised) === topic;
      return matchesTerm && matchesTopic;
    });
  }, [items, query, topic, copy.uncategorised]);

  // Topics are counted across the whole set, not the filtered one: a rail whose numbers
  // moved as you typed would be reporting the search, not the library.
  const topics = useMemo(() => countTopics(items, copy.uncategorised), [items, copy.uncategorised]);

  if (state === 'loading') {
    return <ContentLoader />;
  }

  if (state === 'error') {
    return <ContentMessage title="Something went wrong" body={copy.error} />;
  }

  const isEmpty = filtered.length === 0;
  const emptyNote = <EmptyNote>{items.length === 0 ? copy.empty : copy.noMatch}</EmptyNote>;

  /* --- the news feed ----------------------------------------------------- */

  if (kind === 'news') {
    return (
      <div className="py-12 sm:py-16">
        {/* The statement holds the left half and the controls the right, over one rule.
            Putting the search beside the heading rather than beneath it is what keeps a
            feed of tall dispatches from opening with a column of chrome. */}
        <header className="flex flex-col gap-8 border-b border-rule pb-8 md:flex-row md:items-end md:justify-between">
          <div className="md:w-1/2">
            <h1 className="font-display text-step-4 leading-[1.02] tracking-[-0.02em] text-ink">
              {copy.heading}
            </h1>
            <p className="mt-4 max-w-measure text-step-0 text-ink-muted">{copy.intro}</p>
          </div>

          <div className="flex flex-col gap-5 md:w-1/2 md:items-end">
            <SearchField
              value={query}
              onChange={setQuery}
              label={copy.searchLabel}
              className="w-full max-w-md"
            />
            {topics.length > 1 && (
              <FilterChips
                chips={[
                  { label: copy.allLabel, value: null },
                  ...topics.map((t) => ({ label: t.label, value: t.label })),
                ]}
                selected={topic}
                onSelect={setTopic}
              />
            )}
          </div>
        </header>

        <section className="mt-4">
          {isEmpty
            ? emptyNote
            : filtered.map((item) => <HubItemDispatch key={item.id} item={item} />)}
        </section>
      </div>
    );
  }

  /* --- the article library ----------------------------------------------- */

  return (
    <div className="py-12 sm:py-16">
      <PageHeader title={copy.heading} intro={copy.intro} />

      <div className="mt-12 flex flex-col gap-12 md:flex-row md:gap-16">
        {topics.length > 1 && (
          <TopicRail
            topics={topics}
            selected={topic}
            onSelect={setTopic}
            total={items.length}
            allLabel={copy.allLabel}
          />
        )}

        <section className="min-w-0 flex-1">
          <SearchField
            value={query}
            onChange={setQuery}
            label={copy.searchLabel}
            className="mb-12 w-full max-w-md"
          />

          {isEmpty ? (
            emptyNote
          ) : (
            <div className="grid grid-cols-1 gap-x-12 gap-y-14 lg:grid-cols-2">
              {filtered.map((item) => (
                <HubItemPlate key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default HubArchive;
